import { useMsal } from "@azure/msal-react";
import {
    Box,
    Button,
    Flex,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { CopyToClipboardButton } from "src/components/CopyToClipboardButton";
import { formatDateTimeForCell } from "src/components/MetadataDocuments/columns/columnHelpers";
import { DateRangeFilter } from "src/components/shared/DateRangeFilter";
import { useAuthSearchPagination } from "src/hooks/useAuthSearchPagination";
import { deleteAPI, patchAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const JOBS_BASE = `${BACKEND_URL}/v1/agents/jobs`;

const AGENT_OPTIONS = [
    { value: "report_review", label: "Report review" },
    { value: "fault_procedures", label: "Fault procedures" },
] as const;

const STATUS_OPTIONS = [
    { value: "queued", label: "Queued" },
    { value: "running", label: "Running" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
] as const;

type AgentJobStatus = (typeof STATUS_OPTIONS)[number]["value"];
type AgentType = (typeof AGENT_OPTIONS)[number]["value"];

type AgentJobRow = {
    job_id: string;
    agent: AgentType | string;
    status: AgentJobStatus | string;
    created_at?: string;
    updated_at?: string;
    user_id?: string;
    request?: Record<string, unknown> | null;
    result?: Record<string, unknown> | null;
    error?: string | null;
};

const formatJson = (value: unknown): string => {
    if (value === undefined || value === null) {
        return "";
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const agentDisplayName = (agent: string | undefined): string => {
    if (!agent) return "Agent";
    const known = AGENT_OPTIONS.find((option) => option.value === agent);
    if (known) return known.label;
    return agent
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const useAgentJobOperations = () => {
    const { instance } = useMsal();

    const updateJobStatus = async (jobId: string, status: string) => {
        const response = await patchAPI({
            url: `${JOBS_BASE}/${jobId}`,
            instance,
            payload: { status },
        });
        if (!response.ok) {
            throw new Error("Failed to update job status");
        }
        return response.json();
    };

    const deleteJob = async (jobId: string) => {
        await deleteAPI({
            url: `${JOBS_BASE}/${jobId}`,
            instance,
            expectedStatus: 204,
        });
    };

    return { updateJobStatus, deleteJob };
};

const ManageAgentJobs = () => {
    const columns = useMemo<MRT_ColumnDef<AgentJobRow>[]>(
        () => [
            {
                accessorKey: "job_id",
                header: "Job ID",
                accessorFn: (row) => row?.job_id ?? "",
            },
            {
                accessorKey: "agent",
                header: "Agent",
                accessorFn: (row) => row?.agent ?? "",
                filterVariant: "select",
                mantineFilterSelectProps: {
                    data: AGENT_OPTIONS.map((o) => o.value),
                },
            },
            {
                accessorKey: "status",
                header: "Status",
                accessorFn: (row) => row?.status ?? "",
                filterVariant: "select",
                mantineFilterSelectProps: {
                    data: STATUS_OPTIONS.map((o) => o.value),
                },
            },
            {
                accessorKey: "created_at",
                header: "Created at",
                accessorFn: (row) => (row?.created_at ? new Date(row.created_at) : undefined),
                Cell: ({ cell }) => {
                    const value = cell.getValue<Date | undefined>();
                    return formatDateTimeForCell(value, "");
                },
                Filter: DateRangeFilter,
                sortingFn: "datetime",
            },
            {
                accessorKey: "updated_at",
                header: "Updated at",
                accessorFn: (row) => (row?.updated_at ? new Date(row.updated_at) : undefined),
                Cell: ({ cell }) => {
                    const value = cell.getValue<Date | undefined>();
                    return formatDateTimeForCell(value, "");
                },
                Filter: DateRangeFilter,
                sortingFn: "datetime",
            },
            {
                accessorKey: "user_id",
                header: "User ID",
                accessorFn: (row) => row?.user_id ?? "",
            },
        ],
        [],
    );

    const isAdmin = useAdmin();
    const queryClient = useQueryClient();

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "created_at", desc: true }]);

    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useAuthSearchPagination<AgentJobRow>({
            queryKey: ["agent-jobs"],
            path: "v1/agents/jobs/search",
            columnFilters,
            globalFilter,
            globalFilterKey: "job_id",
            maxResults: 25,
            enabled: isAdmin,
        });

    const { updateJobStatus, deleteJob } = useAgentJobOperations();

    const fetchedJobs = useMemo(() => {
        if (!data?.pages) return [];

        const seen = new Set<string>();
        return data.pages
            .flatMap((page) => page.hits.hits)
            .filter((hit) => {
                const id = hit._source?.job_id || hit._id;
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
            })
            .map((hit) => ({
                ...hit._source,
                job_id: hit._source?.job_id || hit._id,
            }));
    }, [data]);

    const totalJobs = data?.pages[0]?.hits.total.value ?? fetchedJobs.length;

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isFetchingNextPage &&
                hasNextPage
            ) {
                fetchNextPage();
            }
        }
    };

    const [selectedJob, setSelectedJob] = useState<AgentJobRow | null>(null);
    const [detailsModalOpened, setDetailsModalOpened] = useState(false);
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [editStatus, setEditStatus] = useState<string | null>(null);

    const handleOpenDetailsModal = (job: AgentJobRow) => {
        setSelectedJob(job);
        setEditStatus(job.status);
        setDetailsModalOpened(true);
    };

    const handleSaveStatus = async () => {
        if (!selectedJob || !editStatus) return;
        setIsSavingStatus(true);
        try {
            await updateJobStatus(selectedJob.job_id, editStatus);
            notifications.show({
                title: "Success",
                message: "Job status updated",
                color: "green",
            });
            setDetailsModalOpened(false);
            setSelectedJob(null);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["agent-jobs"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to update status",
                color: "red",
            });
        } finally {
            setIsSavingStatus(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            if (!selectedJob) throw new Error("No job selected");
            await deleteJob(selectedJob.job_id);
            notifications.show({
                title: "Success",
                message: "Job deleted successfully",
                color: "green",
            });
            setDeleteModalOpened(false);
            setDetailsModalOpened(false);
            setSelectedJob(null);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["agent-jobs"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to delete job",
                color: "red",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const table = useMantineReactTable({
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        columns: columns as any,
        layoutMode: "grid",
        data: fetchedJobs,
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${totalJobs.toLocaleString()}, (Showing ${table.getFilteredRowModel().rows.length.toLocaleString()})`}
                </Text>
                {isFetching && <Loader size="xs" />}
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row) => row.job_id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        enableStickyHeader: true,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenDetailsModal(row.original),
            style: {
                cursor: isLoading ? "wait" : "pointer",
                opacity: isLoading ? 0.7 : 1,
            },
        }),
        state: {
            isLoading,
            showProgressBars: isFetchingNextPage,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });

    if (!isAdmin) {
        return (
            <Flex px="xl" pb="xl">
                <Text c="dimmed">Admin access is required to manage agent jobs.</Text>
            </Flex>
        );
    }

    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center" justify="space-between">
                    <Title lh={2} c="purple.8" order={2} tt="uppercase">
                        Manage Agent Jobs
                    </Title>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={detailsModalOpened}
                onClose={() => {
                    setDetailsModalOpened(false);
                    setSelectedJob(null);
                }}
                title={`${agentDisplayName(selectedJob?.agent)} Job`}
                size="lg"
            >
                <Stack gap="md">
                    <TextInput
                        label="Job ID"
                        value={selectedJob?.job_id ?? ""}
                        readOnly
                        rightSection={
                            <CopyToClipboardButton
                                content={selectedJob?.job_id}
                                tooltip="Copy job ID"
                                size="sm"
                            />
                        }
                    />
                    <TextInput label="Agent" value={selectedJob?.agent ?? ""} readOnly />
                    <Select
                        label="Status"
                        data={[...STATUS_OPTIONS]}
                        value={editStatus}
                        onChange={setEditStatus}
                    />
                    <TextInput
                        label="Created at"
                        value={
                            selectedJob?.created_at
                                ? formatDateTimeForCell(new Date(selectedJob.created_at), "")
                                : ""
                        }
                        readOnly
                    />
                    <TextInput
                        label="Updated at"
                        value={
                            selectedJob?.updated_at
                                ? formatDateTimeForCell(new Date(selectedJob.updated_at), "")
                                : ""
                        }
                        readOnly
                    />
                    <TextInput
                        label="User ID"
                        value={selectedJob?.user_id ?? ""}
                        readOnly
                        rightSection={
                            <CopyToClipboardButton
                                content={selectedJob?.user_id}
                                tooltip="Copy user ID"
                                size="sm"
                            />
                        }
                    />
                    {(selectedJob?.status === "failed" || !!selectedJob?.error) && (
                        <Textarea
                            label="Error"
                            value={selectedJob?.error || "No error message stored on this job."}
                            readOnly
                            autosize
                            minRows={2}
                            maxRows={8}
                            rightSectionWidth={selectedJob?.error ? 36 : undefined}
                            styles={{
                                input: {
                                    color: "var(--mantine-color-red-8)",
                                    borderColor: "var(--mantine-color-red-3)",
                                },
                            }}
                            rightSection={
                                selectedJob?.error ? (
                                    <CopyToClipboardButton
                                        content={selectedJob.error}
                                        tooltip="Copy error"
                                        size="sm"
                                    />
                                ) : undefined
                            }
                            rightSectionProps={
                                selectedJob?.error
                                    ? { style: { alignItems: "flex-start", paddingTop: 8 } }
                                    : undefined
                            }
                        />
                    )}
                    <Textarea
                        label="Request (JSON)"
                        value={formatJson(selectedJob?.request)}
                        readOnly
                        autosize
                        minRows={4}
                        maxRows={16}
                        rightSectionWidth={36}
                        styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
                        rightSection={
                            <CopyToClipboardButton
                                content={formatJson(selectedJob?.request) || undefined}
                                tooltip="Copy request JSON"
                                size="sm"
                            />
                        }
                        rightSectionProps={{ style: { alignItems: "flex-start", paddingTop: 8 } }}
                    />
                    <Textarea
                        label="Result (JSON)"
                        value={formatJson(selectedJob?.result)}
                        readOnly
                        autosize
                        minRows={4}
                        maxRows={16}
                        rightSectionWidth={36}
                        styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
                        rightSection={
                            <CopyToClipboardButton
                                content={formatJson(selectedJob?.result) || undefined}
                                tooltip="Copy result JSON"
                                size="sm"
                            />
                        }
                        rightSectionProps={{ style: { alignItems: "flex-start", paddingTop: 8 } }}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDetailsModalOpened(false);
                                setSelectedJob(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={isSavingStatus}
                            onClick={handleSaveStatus}
                            disabled={!editStatus || editStatus === selectedJob?.status}
                        >
                            Save status
                        </Button>
                        <Button color="red" onClick={() => setDeleteModalOpened(true)}>
                            Delete Job
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal
                opened={deleteModalOpened}
                onClose={() => setDeleteModalOpened(false)}
                title={`Delete job "${selectedJob?.job_id ?? ""}"?`}
                size="md"
            >
                <Box>
                    <Text mb="md">
                        Are you sure you want to delete this job? This action cannot be undone.
                    </Text>
                    <Flex gap="md" justify="flex-end">
                        <Button variant="outline" onClick={() => setDeleteModalOpened(false)}>
                            Cancel
                        </Button>
                        <Button color="red" loading={isDeleting} onClick={handleDelete}>
                            Delete
                        </Button>
                    </Flex>
                </Box>
            </Modal>
        </>
    );
};

export default ManageAgentJobs;
